import { useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Calendar } from 'lucide-react';

export default function TrendChart({
  title,
  data,
  metrics = [],
  period = 'daily',
  summary,
  chartType = 'line'
}) {
  const [selectedMetric, setSelectedMetric] = useState(metrics[0]?.key);

  // Color mapping for different metrics
  const metricColors = {
    arrivals: '#3B82F6',        // blue-500
    completions: '#10B981',     // green-500
    sla_breaches: '#EF4444',    // red-500
    sla_compliance_rate: '#8B5CF6', // purple-500
    materials_received: '#F59E0B', // amber-500
    avg_processing_time: '#06B6D4', // cyan-500
  };

  // Format tooltip values
  const formatValue = (value, key) => {
    if (key === 'sla_compliance_rate') {
      return `${value}%`;
    }
    if (key === 'avg_processing_time') {
      return `${value} min`;
    }
    return value;
  };

  const ChartComponent = chartType === 'bar' ? BarChart : LineChart;
  const DataComponent = chartType === 'bar' ? Bar : Line;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {period === 'daily' ? 'Daily' : 'Weekly'} performance trends
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-400" />
          <span className="text-sm text-gray-600">
            {period === 'daily' ? 'Last 7 Days' : 'Last 4 Weeks'}
          </span>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-blue-600 font-medium">Total Arrivals</p>
            <p className="text-xl font-bold text-blue-900">{summary.total_arrivals}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <p className="text-xs text-green-600 font-medium">Completions</p>
            <p className="text-xl font-bold text-green-900">{summary.total_completions}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-3">
            <p className="text-xs text-red-600 font-medium">SLA Breaches</p>
            <p className="text-xl font-bold text-red-900">{summary.total_sla_breaches}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-3">
            <p className="text-xs text-purple-600 font-medium">SLA Compliance</p>
            <p className="text-xl font-bold text-purple-900">
              {summary.total_completions > 0
                ? `${Math.round((summary.total_completions - summary.total_sla_breaches) / summary.total_completions * 100)}%`
                : '100%'
              }
            </p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3">
            <p className="text-xs text-amber-600 font-medium">Avg Processing</p>
            <p className="text-xl font-bold text-amber-900">{summary.avg_processing_time || 0} min</p>
          </div>
        </div>
      )}

      {/* Metric Selector */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
        {metrics.map((metric) => (
          <button
            key={metric.key}
            onClick={() => setSelectedMetric(metric.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              selectedMetric === metric.key
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {metric.label}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ChartComponent data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 12 }}
              stroke="#6B7280"
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="#6B7280"
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '0.5rem',
                padding: '0.75rem'
              }}
              formatter={(value, name) => [formatValue(value, name), metrics.find(m => m.key === name)?.label || name]}
            />
            <Legend
              wrapperStyle={{ paddingTop: '1rem' }}
              formatter={(value) => metrics.find(m => m.key === value)?.label || value}
            />

            {selectedMetric ? (
              <DataComponent
                type="monotone"
                dataKey={selectedMetric}
                stroke={metricColors[selectedMetric]}
                fill={metricColors[selectedMetric]}
                strokeWidth={chartType === 'line' ? 2 : 0}
                dot={chartType === 'line' ? { r: 4 } : false}
                activeDot={chartType === 'line' ? { r: 6 } : false}
              />
            ) : (
              metrics.map((metric) => (
                <DataComponent
                  key={metric.key}
                  type="monotone"
                  dataKey={metric.key}
                  stroke={metricColors[metric.key]}
                  fill={metricColors[metric.key]}
                  strokeWidth={chartType === 'line' ? 2 : 0}
                  dot={chartType === 'line' ? { r: 3 } : false}
                />
              ))
            )}
          </ChartComponent>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
